import React from 'react';
import _ from 'lodash';
import { Skeleton } from '@material-ui/lab';
import { makeStyles, withStyles } from '@material-ui/core';
import {InstrumentNameCell} from '../../../../../../../../Modelportfolios/components/InstrumentsList/table-data';
import { withEuroOrDash, withPercentOrDash } from '../../../../../../../../../utils/utils';
import {filterAvailableDiscounts, getBookingDate} from '../../../../../../../../Trades/utils';
import SelectFormElement from '../../../../formElement/SelectFormElement';
import {
  instrumentDataPath,
  SAVINGS_PLANS_KEY,
  SINGLE_INVESTMENTS_KEY,
} from '../../../../../../../mock_produktauswahl';
import WarningTooltip from '../../../../../../../../../components/WarningTooltip';
import { OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG } from '../../../../../../../constants';
import {PortfolioNameCell} from '../../../../../../../../Trades/components/TradeStep/components/PortfolioTrade/table-data';
import {toGermanFormat} from "../../../../../../../../../utils/numberFormater";
import {DATE_FORMAT} from "../../../../../../../../../utils/constants";
import {DisabledReasonTooltip} from "../../../../../../../../../components/Tooltip";
import {
  LimitOrderColumnNew, NumberOfSharesColumnNew, OrderAmountColumn,
  PriceColNew, ShareSettingsColumn
} from "../../../ProductsSelectionSteps/components/InvestmentRecommendationStep/components/Portfolio/table-data";

const useCellStyles = makeStyles(() => ({
  discountTitle: {
    color: '#0092E5',
    fontSize: 10
  },
  amountWithDiscount: {
    color: '#0092E5',
  },
  nowrap: {
    whiteSpace: 'nowrap',
  }
}));

const discountSelectStyles = () => ({
  textFieldRoot: {
    width: 'auto'
  },
  inputRoot: {
    height: 32,
    marginBottom: 0,
    background: '#fff'
  }
});

const filterOptionsByMin = (options, minValue) => {
  // o.value is undefined indicates that None (Keine) discount is used. If discount is not set - it is the same as 0
  return options.filter(o => parseInt(o.value || '0') >= (minValue || 0));
};

const getItemDiscountInfo = (item, options, overallField) => {
  const instrumentData = _.get(options.discounts, instrumentDataPath(item), {});
  let maxDiscount = instrumentData.max_discount || 0;
  const overallValue = options.discounts[overallField];

  let availableDiscounts = options.combinedDiscounts ? options.availableDiscounts : filterOptionsByMin(options.availableDiscounts, Math.min(overallValue, maxDiscount));

  if(options.combinedDiscounts){
    maxDiscount = (maxDiscount || 100) - (options.discounts.overall || 0);
  }
  availableDiscounts = filterAvailableDiscounts(availableDiscounts, maxDiscount);

  const { discount } = instrumentData;

  if ((typeof discount !== 'undefined' && discount !== '') || overallValue !== '') {
    if (availableDiscounts.length > 0) {
      if (!availableDiscounts.find(a => a.value == discount)) {
        options.onItemDiscountChange(item, availableDiscounts[0].value);
      }
    } else if (discount !== '') {
      options.onItemDiscountChange(item, '');
    }
  }

  return { availableDiscounts, discount };
};

const getIndividualOverallDiscountChoices = (options) => {
  return options.combinedDiscounts
      ? filterAvailableDiscounts(options.availableOverAllDiscounts, 100 - (options.discounts.overall || 0))
      : filterOptionsByMin(options.availableOverAllDiscounts, options.discounts.overall);
};

const getTotalItemDiscount = (instrumentData, options) => {
  let discount = 0;
  if(options.discounts.discountsEnabled){
    discount = parseInt(instrumentData.discount) || 0;
    if(options.combinedDiscounts && options.discounts.overall) {
      discount += parseInt(options.discounts.overall)
    }

    discount = discount / 100;
  }

  return discount;
};

const isDiscountAvailable = (options) => !!options.discounts.discountsEnabled && !options.isInvestmentStrategy && !options.isModelportfolio;

const getBrokerFeeInEUR = (amount, broker_fee, discount) => {
  const brokerFee = broker_fee / 100;
  const brokerFeeInEur = amount * brokerFee;

  return brokerFeeInEur - brokerFeeInEur * discount;
};

const TotalFeeCell = ({field, options, items}) => {
  const sum = items.reduce((total, item) => {
    const instrument = item[field];
    if(instrument){
      const instrumentData = _.get(options.discounts, instrumentDataPath(instrument), {});
      const discount = getTotalItemDiscount(instrumentData, options);
      const brokerFeeInEur = getBrokerFeeInEUR(instrument.amount, instrumentData.broker_fee, discount);
      return total + brokerFeeInEur;
    }

    return total
  }, 0);

  return sum ? withEuroOrDash(sum) : ' - ';
};

const AmountWithBookingDate = ({item}) => {
  const bookingDate = getBookingDate(item, DATE_FORMAT);

  return (
    <>
      {withEuroOrDash(item.amount)}
      {!!bookingDate && <div>{bookingDate}</div>}
    </>
  )
};

const TotalAmount = ({items, field}) => {
  const sum = _.sumBy(items, (i) => _.get(i, field, 0));

  return sum ? withEuroOrDash(sum) : ' - ';
};

const SingleAmountColumn = {
  header: [() => <>Einmalanlage<br/>Datum</>],
  body: [({item, options}) => {
    if (options.loading) {
      return <Skeleton />
    } else if (!item.singleInvestment) {
      return ' - ';
    }

    return <AmountWithBookingDate item={item.singleInvestment} />
  }],
  footer: [({items}) => <TotalAmount items={items} field={'singleInvestment.amount'} />],
  footerCellClassName: 'totals-cell',
  align: 'right'
};

const SingleAmountDiscountColumn = {
  header: [({options}) => {

    const disabled = !options.allowedDiscountTypes.includes('single_buy')
      || (options.einzeltitelsOnly && !options.allowedEinzeltitelDiscountTypes.includes('single_buy'));

    return (
      <>
        <div>Einmalanlage<br />Ausgabeaufschlag</div>
        {isDiscountAvailable(options) && (
          <TotalDiscountSelect
            discountKey={SINGLE_INVESTMENTS_KEY}
            disabled={disabled}
            options={options}
            placeholder={'Einmalrabatt'}
          />
        )}
      </>
    )
  }],
  body: [({item, options}) => {
    if (options.loading) {
      return <Skeleton />
    } else if (!item.singleInvestment) {
      return ' - ';
    }
    item = item.singleInvestment;

    const classes = useCellStyles();

    const instrumentData = _.get(options.discounts, instrumentDataPath(item), {});
    const discount = getTotalItemDiscount(instrumentData, options);
    const brokerFeeInEur = getBrokerFeeInEUR(item.amount, instrumentData.broker_fee, discount);

    const disabled = !options.allowedDiscountTypes.includes('single_buy')
      || (!item.is_fund && !options.allowedEinzeltitelDiscountTypes.includes('single_buy'));

    return (
      <>
        <div className={classes.nowrap}>
          <span>{withPercentOrDash(instrumentData.broker_fee, false)}</span><br />
          <span className={discount > 0 ? classes.amountWithDiscount : ''}>
            {withEuroOrDash(brokerFeeInEur)}
          </span>
        </div>

        {isDiscountAvailable(options) && (
          <DiscountSelect
            item={item}
            discountInfo={getItemDiscountInfo(item, options, SINGLE_INVESTMENTS_KEY)}
            disabled={disabled}
            options={options}
            placeholder={'Einmalrabatt'}
          />
        )}
      </>
    )
  }],
  footer: [({options, items}) => <TotalFeeCell options={options} items={items} field={'singleInvestment'} />],
  footerCellClassName: 'totals-cell',
  align: 'right'
};

const TotalDiscountSelect = withStyles(discountSelectStyles)(({classes, discountKey, disabled, options, placeholder}) => (
  <DisabledReasonTooltip title={disabled && OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG}>
    <div>
      <SelectFormElement
        value={options.discounts[discountKey]}
        options={getIndividualOverallDiscountChoices(options)}
        onChange={(val) => options.onDiscountChange(discountKey, val)}
        disabled={disabled}
        placeholder={placeholder}
        custom_classes={classes}
      />
    </div>
  </DisabledReasonTooltip>
));

const DiscountSelect = withStyles(discountSelectStyles)(({classes, item, discountInfo, disabled, options, placeholder}) => (
  <DisabledReasonTooltip title={disabled && OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG}>
    <div>
      <SelectFormElement
        value={discountInfo.discount || ''}
        options={discountInfo.availableDiscounts}
        onChange={val => options.onItemDiscountChange(item, val)}
        disabled={disabled}
        placeholder={placeholder}
        custom_classes={classes}
      />
      {!!options.combinedDiscounts && (
        <div style={{fontSize: 12, marginTop: 5}}>
          <b>Gesamt:</b>&nbsp;
          {toGermanFormat((parseInt(discountInfo.discount) || 0) + (parseInt(options.discounts.overall) || 0), '', ' %', 2, true)}
        </div>
      )}
    </div>
  </DisabledReasonTooltip>
));

const SavingPlanData = ({item}) => {
  const currentPlan = item.savingPlanData;

  return !!currentPlan && (
    <>
      <div>{currentPlan.rotation.label}</div>
      {!!currentPlan.from_date && (
        <div>ab {currentPlan.from_date}</div>
      )}
      {!!currentPlan.till_date && (
        <div>bis {currentPlan.till_date}</div>
      )}
    </>
  )
};

const SparplanColumn = {
  header: [() => <>Sparplan<br/>Turnus</>],
  body: [({item, options}) => {
    if (options.loading) {
      return <Skeleton />
    } else if (!item.savingsPlanItem) {
      return ' - ';
    }

    return (
      <>
        {withEuroOrDash(item.savingsPlanItem.amount)}
        <SavingPlanData item={item.savingsPlanItem} />
      </>
    )
  }],
  footer: [({items}) => <TotalAmount items={items} field={'savingsPlanItem.amount'} />],
  footerCellClassName: 'totals-cell',
  align: 'right'
};

const SparplanDiscountColumn = {
  header: [({options}) => {

    if (options.loading) {
      return null;
    }
    const discountDisabled = !options.allowedDiscountTypes.includes('payment_plan');
    return (
      <>
        <div>Sparplan<br />Ausgabeaufschlag</div>
        {isDiscountAvailable(options) && (
          <TotalDiscountSelect
            discountKey={SAVINGS_PLANS_KEY}
            disabled={discountDisabled}
            options={options}
            placeholder={'Sparplanrabatt'}
          />
        )}
      </>
    )
  }],
  body: [({item, options}) => {
    if (options.loading) {
      return <Skeleton />
    } else if (!item.savingsPlanItem) {
      return ' - ';
    }
    const classes = useCellStyles();
    item = item.savingsPlanItem;

    const instrumentData = _.get(options.discounts, instrumentDataPath(item), {});
    const discount = getTotalItemDiscount(instrumentData, options);
    const brokerFeeInEur = getBrokerFeeInEUR(item.amount, instrumentData.broker_fee, discount);

    return (
      <>
        <div className={classes.nowrap}>
          <span>{withPercentOrDash(instrumentData.broker_fee, false)}</span><br />
          <span className={discount > 0 ? classes.amountWithDiscount : ''}>
            {withEuroOrDash(brokerFeeInEur)}
          </span>
        </div>
        {isDiscountAvailable(options) && (
          <DiscountSelect
            item={item}
            discountInfo={getItemDiscountInfo(item, options, SAVINGS_PLANS_KEY)}
            disabled={!options.allowedDiscountTypes.includes('payment_plan')}
            options={options}
            placeholder={'Sparplanrabatt'}
          />
        )}
      </>
    )
  }],
  footer: [({options, items}) => <TotalFeeCell options={options} items={items} field={'savingsPlanItem'} />],
  footerCellClassName: 'totals-cell',
  align: 'right'
};

const NameCol = {
  header: [({options}) => InstrumentNameCell.header.content(options) || null],
  body: [({item, options}) => InstrumentNameCell.body.content(item.singleInvestment || item.savingsPlanItem, options) || null],
  footer: [() => 'Gesamt'],
  footerCellClassName: 'totals-cell'
};

export const tableStructure = [
  NameCol,
  SingleAmountColumn,
  SingleAmountDiscountColumn,
  SparplanColumn,
  SparplanDiscountColumn
];

export const tableStructureVL = [
  NameCol,
  SparplanColumn,
  SparplanDiscountColumn
];

const LimitOrderColumn = {
  ...LimitOrderColumnNew,
  body: [({item}) => withEuroOrDash(item.singleInvestment.limit)],
};

const NumberOfSharesColumn = {
  ...NumberOfSharesColumnNew,
  body: [({item}) => toGermanFormat(item.singleInvestment.quantity)],
  colSpan: {footer: 5}
};

export const ShareSettingsSummaryBody = ({item}) => (
  <>
    <div>{item.order_type_option.label}</div>
    <div>{item.valid_until_option.label}</div>
    <div>{item.stock_exchange_option.label}</div>
  </>
)

export const tableStructureEinzeltitel = [
  {...NameCol, footer: undefined},
  {...PriceColNew, body: [({item, options}) => PriceColNew.body[0]({item: item.singleInvestment, options})]},
  {
    ...ShareSettingsColumn,
    body: [({item}) => <ShareSettingsSummaryBody item={item.singleInvestment} />]
  },
  LimitOrderColumn,
  NumberOfSharesColumn,
  {
    ...OrderAmountColumn,
    body: [({item, options}) => OrderAmountColumn.body[0]({item: item.singleInvestment, options})],
    footer: [({items}) => <TotalAmount items={items} field={'singleInvestment.transaction_value'}/>],
    footerCellClassName: 'totals-cell',
  }
];

const PortfolioNameColumn = {
   header: [() => (
    <div>
      <span>Produktname</span><br />
      <span style={{fontWeight: 'normal'}}>MP Nummer</span>
    </div>
  )],
  body: [({item, options}) => (
    <PortfolioNameCell
      portfolio={{
        ...item,
        mp_number: item.isInvestmentStrategy ? item.portfolio_id : item.agency_portfolio_id,
        factsheet_link: item.isInvestmentStrategy ? item.factsheet_link : options.modelPortfolioDetailsPath.replace(':id', item.id)
      }}
    />
  )],
  footer: NameCol.footer,
  footerCellClassName: NameCol.footerCellClassName,
};

const PortfolioSingleAmountColumn = {
  header: SingleAmountColumn.header,
  body: [({item, options}) => {
    if (options.loading) {
      return <Skeleton />
    }
    return <AmountWithBookingDate item={item} />
  }],
  footer: [({items}) => <TotalAmount items={items} field={'amount'} />],
  footerCellClassName: SingleAmountColumn.footerCellClassName,
  align: 'right'
};

const getAmountPtfCellParams = (item, options, isPlan) => {
  const fieldKey = isPlan ? SAVINGS_PLANS_KEY : SINGLE_INVESTMENTS_KEY;
  const discount = (options.discounts.discountsEnabled && options.discounts[fieldKey] || 0) / 100;
  const brokerFee = parseFloat(item.fee_brutto || item.cost_brutto || 0);
  const brokerFeeInEur = getBrokerFeeInEUR(isPlan ? item.savingsPlanAmount : item.amount, brokerFee, discount);

  return [fieldKey, discount, brokerFee, brokerFeeInEur];
};

const AmountPortfolioBodyCell = withStyles(discountSelectStyles)(({classes, options, item, isPlan=false}) => {
  const generalClasses = useCellStyles();

  if (options.loading) {
    return <Skeleton />
  }

  const [fieldKey, discount, brokerFee, brokerFeeInEur] = getAmountPtfCellParams(item ,options, isPlan);

  return (
    <>
      <div className={generalClasses.nowrap}>
        <span>
          {options.entryFeeInfoText &&
            <WarningTooltip title={options.entryFeeInfoText}/>
          }
          {withPercentOrDash(brokerFee, false)}
        </span><br />
        <span className={discount > 0 ? generalClasses.amountWithDiscount : ''}>
          {withEuroOrDash(brokerFeeInEur)}
        </span>
      </div>
      {!!options.discounts.discountsEnabled && (
        <SelectFormElement
          value={options.discounts[fieldKey]}
          options={options.availableOverAllDiscounts}
          onChange={(val) => options.onDiscountChange(fieldKey, val)}
          custom_classes={classes}
          placeholder={isPlan ? 'Sparplanrabatt' : 'Einmalrabatt'}
        />
      )}
    </>
  )
});

const AmountPortfolioFooterCell =({items, options, isPlan=false}) => {
  const sum = items.reduce((total, item) => {
    const [fieldKey, discount, brokerFee, brokerFeeInEur] = getAmountPtfCellParams(item, options, isPlan);
    return total + brokerFeeInEur;
  }, 0);

  return sum ? withEuroOrDash(sum) : ' - ';
};

const PortfolioSingleAmountDiscountColumn = {
  header: [() => (<>Einmalanlage<br />Einstiegsentgelt</>)],
  body: [({item, options}) => <AmountPortfolioBodyCell item={item} options={options} />],
  footer: [({options, items}) => <AmountPortfolioFooterCell items={items} options={options} />],
  footerCellClassName: 'totals-cell',
  align: 'right'
};

const PortfolioSparplanColumn = {
  header: [() => (<>Sparplan<br />Turnus</>)],
  body: [({item, options}) => {
    if (options.loading) {
      return <Skeleton />
    }
    return (
      <>
        {withEuroOrDash(item.savingsPlanAmount)}
        <SavingPlanData item={item} />
      </>
    )
  }],
  footer: [({items}) => <TotalAmount items={items} field={'savingsPlanAmount'} />],
  footerCellClassName: SingleAmountColumn.footerCellClassName,
  align: 'right'
};

const PortfolioSparplanDiscountColumn = {
  header: [() => (<>Sparplan<br />Einstiegsentgelt</>)],
  body: [({item, options}) => <AmountPortfolioBodyCell item={item} options={options} isPlan />],
  footer: [({options, items}) => <AmountPortfolioFooterCell items={items} options={options} isPlan />],
  footerCellClassName: 'totals-cell',
  align: 'right'
};

export const tableInvestmentStrategyStructure = [
  PortfolioNameColumn,
  PortfolioSingleAmountColumn,
  PortfolioSingleAmountDiscountColumn,
  PortfolioSparplanColumn,
  PortfolioSparplanDiscountColumn
];